import { useMsal } from '@azure/msal-react';
import { useEffect, useState } from 'react';
import { scopes } from '../../../authConfig';
import { SalesChannel } from '../../../dto/model';
import { strings } from '../../../lang';
import { getLostReasonAssignmentConfig } from '../../../services/lost-reason-service';
import { GetAccessToken } from '../../../utils/auth-utils';
import type { LostReasonSelectOption } from '../modal/customer-lost-modal';

export function legacyLostReasonSelectOptions(): LostReasonSelectOption[] {
  return strings.customerLostOptions.map((o) => ({
    value: String(o.value),
    label: String(o.label),
  }));
}

type UseLostReasonOptionsArgs = {
  /** Reasons are only fetched while enabled (typically while the lost modal is open). */
  enabled: boolean;
  salesChannel?: SalesChannel | null;
  /** Mirrors CaseInfo.configurableLostReasonsInBooking; legacy options are used unless true. */
  configurable?: boolean;
  /** Refetches when the case changes. */
  caseKey?: string;
};

/** Loads the configured lost reasons for a sales channel, falling back to the legacy static list. */
export const useLostReasonOptions = ({ enabled, salesChannel, configurable, caseKey }: UseLostReasonOptionsArgs) => {
  const { instance, inProgress } = useMsal();
  const [options, setOptions] = useState<LostReasonSelectOption[]>([]);
  const [ready, setReady] = useState(false);
  const [configUnavailable, setConfigUnavailable] = useState(false);

  useEffect(() => {
    if (!enabled || !salesChannel) return;
    const applyLegacyFlow = () => {
      setOptions(legacyLostReasonSelectOptions());
      setConfigUnavailable(true);
    };
    if (configurable !== true) {
      setOptions(legacyLostReasonSelectOptions());
      setConfigUnavailable(false);
      setReady(true);
      return;
    }
    let cancelled = false;
    setReady(false);
    setConfigUnavailable(false);
    void (async () => {
      try {
        const token = await GetAccessToken(instance, inProgress, scopes.salesAppointmentApi);
        const res = await getLostReasonAssignmentConfig(token.accessToken, salesChannel);
        if (cancelled) return;
        if (res.isSuccess && res.data?.groups?.length) {
          const lang = strings.getLanguage();
          const opts: LostReasonSelectOption[] = [];
          for (const g of res.data?.groups ?? []) {
            for (const r of g.reasons ?? []) {
              const displayDe = r.germanLabel ?? '';
              const displayEn = r.englishLabel ?? '';
              opts.push({
                value: `${g.id}:${r.id}`,
                label: lang === 'de' ? displayDe : displayEn,
                englishLabel: r.englishLabel,
                outcomeKey: g.outcomeKey,
              });
            }
          }
          if (opts.length > 0) {
            setOptions(opts);
            setConfigUnavailable(false);
          } else {
            applyLegacyFlow();
          }
        } else {
          applyLegacyFlow();
        }
      } catch {
        if (!cancelled) applyLegacyFlow();
      } finally {
        if (!cancelled) setReady(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [enabled, caseKey, salesChannel, configurable, instance, inProgress]);

  return { options, ready, configUnavailable };
};
